// General weather, per day, for the report.
//
// WHAT THIS IS FOR. The console's discipline is "the data you need to make the
// call, and none of the data you don't", and that is why the meteogram carries
// wind, sea, current and visibility and nothing else. But a REPORT is a
// different object from a console: it is forwarded, filed, and read by someone
// who was not at the screen — and a marine operations forecast that says nothing
// about rain, temperature or thunder reads as incomplete to anybody who has ever
// stood on a deck.
//
// WHAT IT IS DELIBERATELY NOT. It is not a synoptic discussion. No fronts, no
// troughs, no "a system approaches Thursday". Not for any commercial reason —
// Dr Bender is SeaState's own metocean lead, so encoding his judgement is the
// premise of this product rather than a conflict with it. The reason is narrower
// and it is this file's whole discipline: a synoptic narrative is a CAUSAL claim
// about a chaotic system, and generating one is fabricating the single thing
// this console refuses to fabricate. Every line here is a measured quantity or a
// stated threshold. Pressure and its tendency are included precisely BECAUSE
// they are the synoptic signal that needs no narrative — a mariner reads a
// falling barometer unaided.
//
// EVERYTHING HERE WAS ALREADY BEING FETCHED. Precipitation, its probability,
// CAPE and the convective set have been pulled on the squall call since squall
// discrimination was built; each was read once to decide a boolean and then
// discarded. Air temperature and cloud came in for the DPR block. So this block
// costs one more parse, not one more request.

import type { PointForecast } from "../forecast/openmeteo.js";
import { convTemp, tempUnit } from "../ui/units.js";

/** Rain that a deck notices, mm in an hour. Below this it is spitting. */
const WET_HOUR_MM = 0.2;
/** Rain heavy enough to take visibility down and make a deck genuinely bad. */
const HEAVY_HOUR_MM = 4;

export interface WeatherDay {
  /** Local date, "YYYY-MM-DD". */
  date: string;
  /** First and last index of this day within the window. */
  from: number;
  to: number;
  /** Air temperature range, already in the operator's unit. */
  tempLo: number | null;
  tempHi: number | null;
  /** Mean cloud cover, %. */
  cloudPct: number | null;
  /** Hours with measurable rain, and the wettest hour's rate (mm/h). */
  wetHours: number;
  peakRainMm: number | null;
  /** Highest hourly precipitation probability, %. */
  peakProb: number | null;
  /** Hours the squall diagnostic flagged, if it ran. */
  squallHours: number | null;
  /** Worst visibility of the day, metres. */
  visLowM: number | null;
  /** Sea-level pressure range, hPa. */
  presLo: number | null;
  presHi: number | null;
  /**
   * The steepest 3-hour pressure CHANGE in the day, hPa, signed — the largest
   * fall wins ties against an equal rise.
   *
   * Tendency is quoted over three hours because that is the interval a barograph
   * and every synoptic code use, so the number is comparable with what a mariner
   * already reads. The sign is kept: a fall is the signal, a rise is reassurance,
   * and an unsigned "change" throws away which one happened.
   */
  tend3h: number | null;
}

const at = (s: readonly (number | null)[] | null | undefined, i: number): number | null =>
  s == null ? null : s[i] ?? null;

function mean(v: number[]): number | null {
  return v.length === 0 ? null : v.reduce((a, b) => a + b, 0) / v.length;
}

/**
 * Group the window's hours into local days and summarise each.
 *
 * `fc.times` are local wall-clock labels, so the day boundary is a string
 * prefix — no timezone arithmetic, and the days break where the operator's day
 * breaks rather than at midnight UTC.
 */
export function weatherDays(fc: PointForecast, a: number, b: number): WeatherDay[] {
  const out: WeatherDay[] = [];
  let cur: WeatherDay | null = null;
  const temps: number[] = [];
  const clouds: number[] = [];

  const flush = (): void => {
    if (cur === null) return;
    cur.tempLo = temps.length ? Math.round(convTemp(Math.min(...temps))) : null;
    cur.tempHi = temps.length ? Math.round(convTemp(Math.max(...temps))) : null;
    const c = mean(clouds);
    cur.cloudPct = c === null ? null : Math.round(c);
    out.push(cur);
    temps.length = 0;
    clouds.length = 0;
  };

  for (let i = Math.max(0, a); i <= Math.min(b, fc.times.length - 1); i++) {
    const day = (fc.times[i] ?? "").slice(0, 10);
    if (cur === null || cur.date !== day) {
      flush();
      cur = {
        date: day, from: i, to: i,
        tempLo: null, tempHi: null, cloudPct: null,
        wetHours: 0, peakRainMm: null, peakProb: null,
        squallHours: fc.squall === null ? null : 0,
        visLowM: null,
        presLo: null, presHi: null, tend3h: null,
      };
    }
    cur.to = i;

    const t = at(fc.airTempC, i);
    if (t !== null) temps.push(t);
    const cl = at(fc.cloudPct, i);
    if (cl !== null) clouds.push(cl);

    const mm = at(fc.precipMm, i);
    if (mm !== null && mm >= WET_HOUR_MM) cur.wetHours++;
    if (mm !== null && (cur.peakRainMm === null || mm > cur.peakRainMm)) cur.peakRainMm = mm;

    const pp = at(fc.precipProb, i);
    if (pp !== null && (cur.peakProb === null || pp > cur.peakProb)) cur.peakProb = pp;

    const vis = at(fc.visibilityM, i);
    if (vis !== null && (cur.visLowM === null || vis < cur.visLowM)) cur.visLowM = vis;

    const pr = at(fc.pressureHpa, i);
    if (pr !== null) {
      if (cur.presLo === null || pr < cur.presLo) cur.presLo = pr;
      if (cur.presHi === null || pr > cur.presHi) cur.presHi = pr;
      // Look BACK three hours rather than forward, so the tendency at hour i is
      // the one an instrument at hour i would have shown.
      const prev = i >= 3 ? at(fc.pressureHpa, i - 3) : null;
      if (prev !== null) {
        const d = pr - prev;
        if (cur.tend3h === null || Math.abs(d) > Math.abs(cur.tend3h) ||
            (Math.abs(d) === Math.abs(cur.tend3h) && d < 0)) {
          cur.tend3h = d;
        }
      }
    }

    // The squall series is the console's own convective diagnostic — CAPE with
    // the cap, the model's convective precipitation, and gust-minus-mean. It is
    // reused rather than re-derived so the report and the meteogram can never
    // disagree about which hours were flagged.
    if (fc.squall !== null && cur.squallHours !== null) {
      const lvl = fc.squall.level?.[i] ?? null;
      if (lvl !== null && lvl > 0) cur.squallHours++;
    }
  }
  flush();
  return out;
}

/** Rain, in the words a deck would use. */
export function rainPhrase(d: WeatherDay): string {
  if (d.peakRainMm === null && d.peakProb === null) return "";
  if (d.wetHours === 0) {
    return d.peakProb !== null && d.peakProb >= 20 ? `none (peak ${Math.round(d.peakProb)}% chance)` : "none";
  }
  const heavy = d.peakRainMm !== null && d.peakRainMm >= HEAVY_HOUR_MM;
  return `${d.wetHours} h${heavy ? ", heavy" : ""} (peak ${(d.peakRainMm ?? 0).toFixed(1)} mm/h)`;
}

/** Temperature range in the operator's unit, or "" when absent. */
export function tempPhrase(d: WeatherDay): string {
  if (d.tempLo === null || d.tempHi === null) return "";
  return `${d.tempLo}–${d.tempHi} ${tempUnit()}`;
}

/**
 * Pressure and its steepest 3-hour tendency: "1013–1019 hPa · falling 2.1/3h".
 *
 * 3 hPa in three hours is the classic "wind is coming" threshold a mariner is
 * taught, so it is called out — but as a stated fact about the barometer, not as
 * a forecast of what the wind will do. That inference belongs to a forecaster.
 */
export function pressurePhrase(d: WeatherDay): string {
  if (d.presLo === null || d.presHi === null) return "";
  const range = d.presLo === d.presHi
    ? `${Math.round(d.presLo)} hPa`
    : `${Math.round(d.presLo)}–${Math.round(d.presHi)} hPa`;
  if (d.tend3h === null || Math.abs(d.tend3h) < 0.5) return `${range} · steady`;
  const word = d.tend3h < 0 ? "falling" : "rising";
  const sharp = Math.abs(d.tend3h) >= 3 ? " sharply" : "";
  return `${range} · ${word}${sharp} ${Math.abs(d.tend3h).toFixed(1)}/3h`;
}

/** Worst visibility as a distance, since the report has room for the number. */
export function visPhrase(d: WeatherDay): string {
  if (d.visLowM === null) return "";
  return d.visLowM >= 10000 ? "> 10 km" : `${(d.visLowM / 1000).toFixed(1)} km`;
}

export const GENWEATHER_NOTE =
  "Pressure tendency is the steepest 3-hour change of the day. Thunder hours come " +
  "from the convective diagnostic that flags squalls on the meteogram. Rain and " +
  "cloud are single-model (GFS), not the multi-model spread used for wind and sea " +
  "above — read them as indicative rather than graded.";
